import React, { useState, useEffect, useCallback } from 'react';
import type { CurrencyData } from './FundamentalsData';

const STORAGE_KEY = 'tts_fundamentals_v2';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

async function fetchFundamentals(): Promise<CurrencyData[]> {
  const res = await fetch(`${window.__LIVE_API_BASE || ''}/api/fundamentals`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return json.fundamentals as CurrencyData[];
}

function timeAgo(ts: number): string {
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmt(v: number | null | undefined, d = 2): string {
  if (v == null) return '—';
  return v.toFixed(d);
}

const COLUMNS = [
  { key: 'interestRate',    label: 'Interest Rate',   changeKey: 'interestRateChange',    format: v => fmt(v) + '%',    colorize: 'rate' },
  { key: 'inflation',       label: 'Inflation (YoY)', changeKey: 'inflationChange',       format: v => fmt(v) + '%',    colorize: 'change' },
  { key: 'gdp',             label: 'GDP Growth',       changeKey: 'gdpChange',             format: v => fmt(v) + '%',    colorize: 'change' },
  { key: 'unemployment',    label: 'Unemployment',     changeKey: 'unemploymentChange',    format: v => fmt(v, 1) + '%', colorize: 'inv' },
  { key: 'cotLong',         label: 'COT Long %',       changeKey: null,                    format: v => fmt(v),          colorize: 'cot' },
  { key: 'cotShort',        label: 'COT Short %',      changeKey: null,                    format: v => fmt(v),          colorize: 'cot' },
] as const;

function deltaColor(v: number | null | undefined, invert = false): string {
  if (v == null) return '#4a5568';
  if (invert) return v > 0 ? '#ef4444' : v < 0 ? '#22c55e' : '#94a3b8';
  return v > 0 ? '#22c55e' : v < 0 ? '#ef4444' : '#94a3b8';
}

function COTCell({ long, short }: { long: number | null; short: number | null }) {
  if (long == null && short == null) return <span style={styles.noData}>—</span>;
  const l = long ?? 0;
  const s = short ?? 0;
  const total = l + s || 1;
  const lw = (l / total) * 100;
  return (
    <div style={styles.cotWrap}>
      <div style={styles.cotTrack}>
        <div style={{ ...styles.cotFill, width: `${lw}%` }} />
      </div>
      <div style={styles.cotText}>
        <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 700 }}>{l > 0 ? `${l.toFixed(1)}` : '—'}</span>
        <span style={{ color: '#64748b', fontSize: 9 }}>/</span>
        <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 700 }}>{s > 0 ? `${s.toFixed(1)}` : '—'}</span>
      </div>
    </div>
  );
}

export default function CurrenciesPanel() {
  const [data, setData] = useState<CurrencyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cacheAge, setCacheAge] = useState<number | null>(null);
  const [sortCol, setSortCol] = useState<string>('code');
  const [sortAsc, setSortAsc] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchFundamentals();
      setData(result);
      const now = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ data: result, ts: now }));
      setCacheAge(now);
    } catch (e) {
      console.error('Failed to load fundamentals', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        const { data: cachedData, ts } = JSON.parse(cached);
        const ageMs = Date.now() - ts;
        if (ageMs < CACHE_TTL_MS && Array.isArray(cachedData) && cachedData.length > 0) {
          setData(cachedData);
          setCacheAge(ts);
          setLoading(false);
          return;
        }
      } catch { /* corrupt */ }
    }
    load();
  }, [load]);

  // Tick "X ago" every minute
  useEffect(() => {
    if (!cacheAge) return;
    const id = setInterval(() => setCacheAge(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [cacheAge]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const sorted = [...data].sort((a, b) => {
    const av = (a as any)[sortCol] ?? -Infinity;
    const bv = (b as any)[sortCol] ?? -Infinity;
    if (av < bv) return sortAsc ? -1 : 1;
    if (av > bv) return sortAsc ? 1 : -1;
    return a.code.localeCompare(b.code);
  });

  const handleSort = (key: string) => {
    if (sortCol === key) setSortAsc(!sortAsc);
    else { setSortCol(key); setSortAsc(true); }
  };

  const sortIcon = (key: string) =>
    sortCol !== key ? '⇅' : sortAsc ? '↑' : '↓';

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div style={styles.spinner} />
        <div style={styles.loadingText}>Loading currency data...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.title}>💱 Currency Fundamentals</div>
          {cacheAge && (
            <div style={styles.updated}>Updated {timeAgo(cacheAge)}</div>
          )}
        </div>
        <div style={styles.headerRight}>
          <div style={styles.sourceNote}>World Bank · Forex Factory · CFTC</div>
          <button style={styles.refreshBtn} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? '⟳ Refreshing...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableOuter}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.th}>
              <th style={{ ...styles.thCell, textAlign: 'left', minWidth: 80 }}>
                Currency {sortCol === 'code' ? sortIcon('code') : ''}
              </th>
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  style={styles.thCell}
                  onClick={() => handleSort(col.key)}
                >
                  <div>{col.label}</div>
                  <div style={styles.sortHint}>{sortIcon(col.key)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((cur, i) => (
              <tr
                key={cur.code}
                style={{ ...styles.tr, background: i % 2 === 0 ? 'transparent' : 'rgba(41,52,255,0.025)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(41,52,255,0.07)')}
                onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(41,52,255,0.025)')}
              >
                {/* Currency */}
                <td style={{ ...styles.td, textAlign: 'left', paddingLeft: 14 }}>
                  <div style={styles.currencyCode}>{cur.code}</div>
                  <div style={styles.currencyName}>{cur.name}</div>
                </td>

                {/* Interest Rate */}
                <td style={styles.td}>
                  <div style={styles.mainValue}>{fmt(cur.interestRate)}%</div>
                  {cur.interestRateChange != null && (
                    <div style={{ ...styles.delta, color: deltaColor(cur.interestRateChange) }}>
                      {cur.interestRateChange >= 0 ? '+' : ''}{cur.interestRateChange.toFixed(2)}%
                    </div>
                  )}
                </td>

                {/* Inflation */}
                <td style={styles.td}>
                  <div style={styles.mainValue}>{fmt(cur.inflation)}%</div>
                  {cur.inflationChange != null && (
                    <div style={{ ...styles.delta, color: deltaColor(cur.inflationChange) }}>
                      {cur.inflationChange >= 0 ? '+' : ''}{cur.inflationChange.toFixed(2)}%
                    </div>
                  )}
                </td>

                {/* GDP */}
                <td style={styles.td}>
                  <div style={styles.mainValue}>{fmt(cur.gdp)}%</div>
                  {cur.gdpChange != null && (
                    <div style={{ ...styles.delta, color: deltaColor(cur.gdpChange) }}>
                      {cur.gdpChange >= 0 ? '+' : ''}{cur.gdpChange.toFixed(2)}%
                    </div>
                  )}
                </td>

                {/* Unemployment */}
                <td style={styles.td}>
                  <div style={styles.mainValue}>{fmt(cur.unemployment, 1)}%</div>
                  {cur.unemploymentChange != null && (
                    <div style={{ ...styles.delta, color: deltaColor(cur.unemploymentChange, true) }}>
                      {cur.unemploymentChange >= 0 ? '+' : ''}{cur.unemploymentChange.toFixed(1)}%
                    </div>
                  )}
                </td>

                {/* COT Long */}
                <td style={styles.td}>
                  <COTCell long={cur.cotLong} short={cur.cotShort} />
                </td>

                {/* COT Short */}
                <td style={styles.td}>
                  {cur.cotLong != null && cur.cotShort != null ? (
                    <div style={{
                      ...styles.netBadge,
                      color: cur.cotLong - cur.cotShort >= 0 ? '#22c55e' : '#ef4444',
                    }}>
                      Net {cur.cotLong - cur.cotShort >= 0 ? '+' : ''}{(cur.cotLong - cur.cotShort).toFixed(1)}
                    </div>
                  ) : (
                    <span style={styles.noData}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length === 0 && !loading && (
        <div style={styles.empty}>No data. Click Refresh to load.</div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: '#e8e6e0',
    letterSpacing: '0.02em',
  },
  updated: {
    fontSize: 10,
    color: '#4a5568',
    marginTop: 3,
  },
  headerRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 5,
  },
  sourceNote: {
    fontSize: 10,
    color: '#334155',
    fontStyle: 'italic',
  },
  refreshBtn: {
    padding: '6px 16px',
    borderRadius: 8,
    border: '1px solid rgba(41,52,255,0.3)',
    background: 'rgba(41,52,255,0.08)',
    color: '#6b7aff',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  tableOuter: {
    borderRadius: 12,
    border: '1px solid rgba(41,52,255,0.12)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  },
  th: {
    background: 'rgba(18,20,38,0.95)',
    borderBottom: '1px solid rgba(41,52,255,0.2)',
  },
  thCell: {
    padding: '10px 14px',
    color: '#4a5568',
    fontWeight: 600,
    fontSize: 11,
    textAlign: 'right',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  sortHint: {
    fontSize: 9,
    color: '#2a3050',
    marginTop: 1,
  },
  tr: {
    borderBottom: '1px solid rgba(41,52,255,0.06)',
    transition: 'background 0.15s',
  },
  td: {
    padding: '10px 14px',
    textAlign: 'right',
    verticalAlign: 'middle',
  },
  currencyCode: {
    fontSize: 13,
    fontWeight: 800,
    color: '#e8e6e0',
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: '0.05em',
  },
  currencyName: {
    fontSize: 9,
    color: '#4a5568',
    marginTop: 1,
  },
  mainValue: {
    fontSize: 13,
    fontWeight: 700,
    color: '#e8e6e0',
    fontFamily: "'JetBrains Mono', monospace",
  },
  delta: {
    fontSize: 10,
    fontWeight: 600,
    marginTop: 2,
    fontFamily: "'JetBrains Mono', monospace",
  },
  cotWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    alignItems: 'flex-end',
  },
  cotTrack: {
    width: 64,
    height: 5,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 100,
    overflow: 'hidden',
  },
  cotFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #16a34a, #22c55e)',
    borderRadius: 100,
  },
  cotText: {
    display: 'flex',
    gap: 3,
    alignItems: 'center',
  },
  netBadge: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "'JetBrains Mono', monospace",
  },
  noData: {
    color: '#334155',
    fontSize: 12,
  },
  loadingWrap: {
    padding: 60,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  spinner: {
    width: 28,
    height: 28,
    border: '3px solid rgba(41,52,255,0.2)',
    borderTopColor: '#2934ff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    color: '#64748b',
    fontSize: 13,
    fontWeight: 600,
  },
  empty: {
    padding: 28,
    textAlign: 'center',
    color: '#3a4055',
    fontSize: 13,
  },
};
